import { Directive, Input, OnDestroy, TemplateRef, ViewContainerRef } from '@angular/core';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AccountACL } from '../constants/account-acl.constants';
import { AppState } from '../state/app.state';
import { userHasAnyACL } from '../state/user.selectors';

/**
 * Has any Access control list
 * @description Angular Directive. Conditionally includes an HTML element if current user has any
 * of the Access control list passed as the `expression`.
 *
 * @howToUse
 * ```
 *     <some-element *apHasAnyAcl="'CHANGE_PASSWORD_VIEW'">...</some-element>
 *
 *     <some-element *apHasAnyAcl="['CHANGE_PASSWORD_VIEW', 'CHANGE_PASSWORD_UPDATE']">...</some-element>
 * ```
 */
@Directive({
  selector: '[apHasAnyAcl]',
})
export class HasAnyAclDirective implements OnDestroy {
  acls: AccountACL[] = [];
  destroy$: Subject<boolean> = new Subject<boolean>();

  constructor(private readonly templateRef: TemplateRef<unknown>, private readonly viewContainerRef: ViewContainerRef, private readonly store: Store<AppState>) { }

  /**
   * Hide or show element based on current ACL
   * @param value Single or multiple ACL to check
   */
  @Input()
  set apHasAnyAcl(value: AccountACL | AccountACL[]) {
    this.acls = typeof value === 'string' ? [value] : value;
    this.updateView(false);
    // Get notified each time authentication state changes.
    this.store
      .select(userHasAnyACL, { acls: value })
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: boolean) => this.updateView(res));
  }

  /**
   * Hide element than show again if user have a specified ACL
   * @param hasAnyAcl True if user have the ACL to see the element
   */
  private updateView(hasAnyAcl: boolean): void {
    this.viewContainerRef.clear();
    if (hasAnyAcl) {
      this.viewContainerRef.createEmbeddedView(this.templateRef);
    }
  }

  /**
   * @ignore
   */
  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
